// src/context/BetSlipContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { BetAPI } from '../services/api';

const BetSlipContext = createContext();

export const useBetSlip = () => {
  const context = useContext(BetSlipContext);
  if (!context) {
    throw new Error('useBetSlip must be used within a BetSlipProvider');
  }
  return context;
};

export const BetSlipProvider = ({ children }) => {
  const [bets, setBets] = useState([]);
  const [totalOdds, setTotalOdds] = useState(1);
  const [betslipCount, setBetslipCount] = useState(0);
  const [isPlacing, setIsPlacing] = useState(false);
  const [error, setError] = useState(null);
  const [betMode, setBetMode] = useState('singles'); // 'singles' or 'accumulator'

  // Load bets from localStorage on mount
  useEffect(() => {
    const savedBets = JSON.parse(localStorage.getItem('betslip') || '[]');
    const savedMode = localStorage.getItem('betslip_mode') || 'singles';
    setBets(savedBets);
    setBetslipCount(savedBets.length);
    setBetMode(savedMode);
  }, []);

  // Save bets to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('betslip', JSON.stringify(bets));
    setBetslipCount(bets.length);
    
    // Calculate total odds (for accumulator)
    if (bets.length > 0) {
      const odds = bets.reduce((acc, bet) => acc * bet.odds, 1);
      setTotalOdds(odds);
    } else {
      setTotalOdds(1);
    }
  }, [bets]);

  // Switch bet mode
  const switchBetMode = (mode) => {
    setBetMode(mode);
    localStorage.setItem('betslip_mode', mode);
  };

  const addToBetSlip = (bet) => {
    const betId = `${bet.matchId}-${bet.selection}-${Date.now()}`;
    const newBet = {
      id: betId,
      ...bet,
      addedAt: new Date().toISOString()
    };
    
    setBets(prev => {
      // Check if similar bet already exists
      const exists = prev.some(b => 
        b.matchId === bet.matchId && 
        b.selection === bet.selection
      );
      
      if (exists) {
        // Update existing bet
        return prev.map(b => 
          b.matchId === bet.matchId && b.selection === bet.selection 
            ? { ...b, ...bet, addedAt: new Date().toISOString() }
            : b
        );
      }
      
      return [...prev, newBet];
    });
    
    return true;
  };

  const removeFromBetSlip = (betId) => {
    setBets(prev => prev.filter(bet => bet.id !== betId));
  };

  const clearBetSlip = () => {
    setBets([]);
    localStorage.removeItem('betslip');
  };

  const updateBetStake = (betId, stake) => {
    setBets(prev => prev.map(bet => 
      bet.id === betId ? { ...bet, stake: parseFloat(stake) || 0 } : bet
    ));
  };

  const calculatePotentialWin = (stake) => {
    if (!stake || bets.length === 0) return 0;
    return parseFloat(stake) * totalOdds;
  };

  // Get total stake (for accumulator, it's a single stake)
  const getTotalStake = () => {
    if (betMode === 'accumulator') {
      // For accumulator, get the stake from the first bet (all bets share one stake)
      return bets.length > 0 ? parseFloat(bets[0]?.stake) || 0 : 0;
    } else {
      // For singles, sum all stakes
      return bets.reduce((total, bet) => total + (parseFloat(bet.stake) || 0), 0);
    }
  };

  // Place bet - handles both singles and accumulator
  const placeBet = async (betData) => {
    setIsPlacing(true);
    setError(null);
    
    try {
      // If accumulator mode, send all selections as one bet
      if (betMode === 'accumulator' && betData.isAccumulator) {
        const response = await BetAPI.placeAccumulatorBet(betData);
        
        if (response.status === 200 || response.status === 201) {
          clearBetSlip();
          setIsPlacing(false);
          return { success: true, bet: response, isAccumulator: true };
        } else {
          const errorMsg = response.error || 'Failed to place accumulator bet';
          setError(errorMsg);
          setIsPlacing(false);
          return { success: false, error: errorMsg };
        }
      } else {
        // Single bet
        const response = await BetAPI.placeBet(betData);
        
        if (response.status === 200 || response.status === 201) {
          setIsPlacing(false);
          return { success: true, bet: response };
        } else {
          const errorMsg = response.error || 'Failed to place bet';
          setError(errorMsg);
          setIsPlacing(false);
          return { success: false, error: errorMsg };
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'An error occurred while placing your bet';
      setError(errorMsg);
      setIsPlacing(false);
      return { success: false, error: errorMsg };
    }
  };

  // Place multiple singles
  const placeMultipleSingles = async (betsData) => {
    setIsPlacing(true);
    setError(null);
    
    try {
      const results = [];
      const errors = [];

      for (const betData of betsData) {
        try {
          const response = await BetAPI.placeBet(betData);
          if (response.status === 200 || response.status === 201) {
            results.push({ success: true, bet: response });
          } else {
            const errorMsg = response.error || 'Failed to place bet';
            errors.push(errorMsg);
            results.push({ success: false, error: errorMsg });
          }
        } catch (err) {
          const errorMsg = err.message || 'Failed to place bet';
          errors.push(errorMsg);
          results.push({ success: false, error: errorMsg });
        }
      }

      const allSuccessful = results.every(r => r.success);
      
      if (allSuccessful) {
        clearBetSlip();
      }

      setIsPlacing(false);
      return { 
        success: allSuccessful, 
        results,
        errors,
        total: results.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length
      };
      
    } catch (err) {
      const errorMsg = err.message || 'An error occurred';
      setError(errorMsg);
      setIsPlacing(false);
      return { success: false, error: errorMsg };
    }
  };

  // Cancel a bet using API
  const cancelBet = async (betId) => {
    try {
      const response = await BetAPI.cancelBet(betId);
      
      if (response.status === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.error || 'Failed to cancel bet' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Get bet history using API
  const getBetHistory = async (params = {}) => {
    try {
      const response = await BetAPI.getBetHistory(params);
      
      if (response.status === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.error || 'Failed to load bet history' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return (
    <BetSlipContext.Provider value={{
      bets,
      totalOdds,
      betslipCount,
      isPlacing,
      error,
      betMode,
      addToBetSlip,
      removeFromBetSlip,
      clearBetSlip,
      updateBetStake,
      calculatePotentialWin,
      placeBet,
      placeMultipleSingles,
      cancelBet,
      getBetHistory,
      switchBetMode,
      getTotalStake
    }}>
      {children}
    </BetSlipContext.Provider>
  );
};